import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { Loader2, X, Save, Download } from "lucide-react";

const LS_TOKEN = "bnm-admin-token";
const FILTERS = [
  { key: "all", label: "All" },
  { key: "free", label: "Free readers" },
  { key: "book", label: "Book owners" },
  { key: "vault", label: "Vault members" },
  { key: "orientation", label: "Orientation" },
  { key: "immersion", label: "Immersion" },
];
const TIER_LABEL = { BOOK_OWNER: "Book", VAULT_MEMBER: "Vault", ORIENTATION_CLIENT: "Orientation", IMMERSION_CLIENT: "Immersion" };
const TAG_LABEL = {
  "founding-era": "Founding era", recent: "Recent", business: "Business",
  individual: "Individual", "phone-on-file": "Phone on file",
  "date-verified": "Date verified", "date-estimated": "Date estimated",
};
const tagLabel = (t) => TAG_LABEL[t] || (t.startsWith("cohort-") ? t.replace("cohort-", "Joined ") : t);
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AdminReaders() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [filter, setFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [tagOptions, setTagOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (f, t) => {
    try {
      const q = `filter=${f}${t ? `&tag=${encodeURIComponent(t)}` : ""}`;
      const { data } = await axios.get(`${API}/admin/readers?${q}`, auth);
      setRows(data.readers || []);
    }
    catch (e) { setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load readers."); }
  }, [token]);

  useEffect(() => { if (token) load(filter, tagFilter); else setErr("Please sign in at /admin first."); }, [token, filter, tagFilter, load]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/reader-tags`, auth).then(({ data }) => setTagOptions(data.tags || [])).catch(() => {});
  }, [token]);

  const openReader = async (email) => {
    try { const { data } = await axios.get(`${API}/admin/readers/${encodeURIComponent(email)}`, auth); setSel(data); setNotes(data.notes || ""); }
    catch (e) { /* ignore */ }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const q = `filter=${filter}${tagFilter ? `&tag=${encodeURIComponent(tagFilter)}` : ""}`;
      const { data } = await axios.get(`${API}/admin/readers/export.csv?${q}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `readers-${tagFilter || filter}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ } finally { setExporting(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await axios.patch(`${API}/admin/readers/${encodeURIComponent(sel.email)}`, { notes }, auth); setSel({ ...sel, notes }); setNotesSaved(true); setTimeout(() => setNotesSaved(false), 1800); }
    catch (e) { /* ignore */ } finally { setSavingNotes(false); }
  };

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="readers-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-readers">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <AdminNav />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl text-ink">Readers & Leads</h1>
          <button type="button" onClick={exportCsv} disabled={exporting} data-testid="readers-export-btn"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export {tagFilter ? tagLabel(tagFilter) : (filter === "all" ? "all" : filter)} CSV
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`readers-filter-${f.key}`}
              className={`rounded-full px-3.5 py-1.5 text-sm transition ${filter === f.key ? "bg-ink text-ivory-50" : "bg-ivory-50 text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {tagOptions.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="readers-tag-filters">
            <span className="mr-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">Smart tags</span>
            {tagFilter && (
              <button type="button" onClick={() => setTagFilter("")} data-testid="readers-tag-clear"
                className="rounded-full bg-ink px-3 py-1 text-xs text-ivory-50">Clear</button>
            )}
            {tagOptions.map((t) => (
              <button key={t.tag} type="button" onClick={() => setTagFilter(tagFilter === t.tag ? "" : t.tag)} data-testid={`readers-tag-${t.tag}`}
                className={`rounded-full px-3 py-1 text-xs transition ${tagFilter === t.tag ? "bg-gold-deep text-ivory-50" : "bg-ivory-50 text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink"}`}>
                {tagLabel(t.tag)} <span className="opacity-60">{t.count}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 overflow-hidden rounded-2xl bg-ivory-50 shadow-sm ring-1 ring-ivory-400/60">
          {rows.length === 0 ? (
            <p className="p-6 text-sm text-ink-muted">No readers in this view yet.</p>
          ) : rows.map((r) => (
            <button key={r.email} type="button" onClick={() => openReader(r.email)} data-testid={`reader-row-${r.email}`}
              className="flex w-full items-center justify-between gap-4 border-b border-ivory-400/50 px-5 py-3.5 text-left last:border-0 hover:bg-gold/5">
              <div className="min-w-0">
                <p className="truncate font-display text-ink">{r.first_name || r.email}</p>
                <p className="truncate text-xs text-ink-muted">{r.email}</p>
                {(r.tags && r.tags.length > 0) && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.tags.filter((t) => t !== "individual" && t !== "date-verified" && t !== "date-estimated").slice(0, 3).map((t) => (
                      <span key={t} className="rounded-full bg-gold/10 px-1.5 py-0.5 text-[10px] text-gold-deep">{tagLabel(t)}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-none items-center gap-1.5">
                {r.tiers.length ? r.tiers.map((t) => (
                  <span key={t} className="rounded-full bg-sage/20 px-2 py-0.5 text-[11px] text-sage-deep">{TIER_LABEL[t] || t}</span>
                )) : <span className="rounded-full bg-ivory-200 px-2 py-0.5 text-[11px] text-ink-muted">Free</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Reader detail drawer */}
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={() => setSel(null)} data-testid="reader-drawer">
          <div className="h-full w-full max-w-md overflow-y-auto bg-ivory-50 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display text-2xl text-ink">{sel.first_name || sel.email}</h2>
                <p className="text-sm text-ink-muted">{sel.email}</p>
              </div>
              <button type="button" onClick={() => setSel(null)} data-testid="reader-drawer-close" className="rounded-full p-1.5 text-ink-soft hover:text-ink"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(sel.tiers || []).map((t) => <span key={t} className="rounded-full bg-sage/20 px-2.5 py-0.5 text-xs text-sage-deep">{TIER_LABEL[t] || t}</span>)}
              {(sel.tags || []).map((t) => <span key={t} className="rounded-full bg-gold/10 px-2.5 py-0.5 text-xs text-gold-deep" data-testid={`reader-drawer-tag-${t}`}>{tagLabel(t)}</span>)}
            </div>

            <section className="mt-6">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Orders</p>
              {sel.orders.length === 0 ? <p className="mt-1 text-sm text-ink-muted">None.</p> : sel.orders.map((o) => (
                <div key={o.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink" data-testid="reader-order">
                  {(o.items || []).map((i) => i.title).join(", ") || o.offer} · {usd(o.total_cents)}
                </div>
              ))}
            </section>

            <section className="mt-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Subscriptions</p>
              {sel.subscriptions.length === 0 ? <p className="mt-1 text-sm text-ink-muted">None.</p> : sel.subscriptions.map((s) => (
                <div key={s.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink">
                  {s.plan_name} · <span className="text-ink-soft">{s.status}</span>
                </div>
              ))}
            </section>

            <section className="mt-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Reflections</p>
              {sel.reflections.length === 0 ? <p className="mt-1 text-sm text-ink-muted">None.</p> : sel.reflections.map((r) => (
                <div key={r.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink">{r.reader_name || "Reflection"} · <span className="text-ink-soft">{r.status}</span></div>
              ))}
            </section>

            <section className="mt-6">
              <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">Private notes</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="reader-notes"
                className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
              <button type="button" onClick={saveNotes} disabled={savingNotes} data-testid="reader-notes-save"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save notes
              </button>
              {notesSaved && <span className="ml-2 text-sm text-sage-deep" data-testid="reader-notes-saved">Saved</span>}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
